import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AuthenticationErrorMessage from '../../components/AuthenticationErrorMessage/AuthenticationErrorMessage';
import { PARAM_AUTH_REQ_ID, PARAM_CODE, PARAM_STATE } from '../../components/AuthenticationWrapper';
import ConsentField from '../../components/ConsentField/ConsentField';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { extractGovernmentIdFromSubClaim } from '../../helpers/jwt';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { getAccessToken, initAuthentication, selectValidAccessToken } from '../../store/authentication';
import { setGovernmentIdPageIndex, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';
import { CountryCode } from '../../types/CountryCode';

const EskatKreditStatusConsentPageDK = () => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage, pageAction, numberOfPages } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const [params, setParams] = useSearchParams();
    useSetBannerType('bannerOne');
    useComponentTracker('EskatKreditStatusConsentPage');

    const {
        consentDetails,
        isError: isConsentError,
        isLoading: isConsentLoading,
    } = useGetConsentService('TERMS_CREDIT_STATUS');
    // Every consent shown on this page is mandatory to proceed, regardless of the `required` flag
    // consent-service happens to attach -- this page exists solely to obtain the eSkat/KreditStatus consent.
    //
    // `INFORMATION` consents are excluded because `ConsentField` renders them as prose with no checkbox: a
    // name in this list that no input can ever satisfy makes the page permanently impassable, matching
    // CoApplicantEskatKreditStatusConsentPageDK.
    const consentsToAccept = consentDetails?.filter((consentDetail) => consentDetail.displayType !== 'INFORMATION');
    const requiredFieldConsents = consentsToAccept?.map((consentDetail) => consentDetail.name) ?? [];
    const validationErrors = useValidation(requiredFieldConsents);
    // The gate has to be positive: consent is only real if we actually rendered something to consent to.
    // Deriving it from `validationErrors` alone fails OPEN -- an empty `consentsToAccept` (service still
    // loading, service errored, or a 200 carrying no proposals) leaves nothing to validate, so
    // `validationErrors` would be empty even though nothing was ever shown to consent to. Matches the
    // co-applicant page's guard.
    const hasConsentToAccept = !isConsentLoading && !isConsentError && !!consentsToAccept?.length;
    // Deliberately the real access token only, matching VerifyIncomePageNO/CoApplicantLogInPage, which
    // perform their own real authentication exchange even while demo mode is active elsewhere in the app.
    const accessToken = useAppSelector(selectValidAccessToken);
    const applicantGovernmentIdFieldState = useAppSelector(selectFieldStateByName)(
        AttributeNames.applicantGovernmentId,
    );
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        if (accessToken) {
            return;
        }
        if (params.get(PARAM_CODE) && params.get(PARAM_STATE)) {
            dispatch(getAccessToken({ state: params.get(PARAM_STATE)!, code: params.get(PARAM_CODE)! }));
            params.delete(PARAM_CODE);
            params.delete(PARAM_STATE);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        }
    }, [params, accessToken]);

    useEffect(() => {
        // Record where this page sits in the current wizard so SubmitPageDK can route back here if
        // applicantGovernmentId ends up missing at submit, without hard-coding an index -- mirrors
        // VerifyIncomePageNO's identical use of this field for NO/SE (LP-2699/LP-2980).
        //
        // Clamped against numberOfPages (matching Pages.tsx's own selection formula exactly) rather than
        // recording the raw currentPage, for defensive parity with the co-applicant page's identical fix --
        // SubmitApplicationWizardDK renders one fixed-length page list, so this isn't currently reachable
        // here the way it is for CoApplicantEskatKreditStatusConsentPageDK/SubmitCoApplicantWizardDK's
        // state-branched (short vs. authenticated) lists, but the clamp is a no-op whenever currentPage is
        // already in range, so there's no cost to applying it here too (LP-2980).
        dispatch(setGovernmentIdPageIndex(Math.min(Math.max(currentPage, 0), numberOfPages - 1)));
    }, [dispatch, currentPage, numberOfPages]);

    useEffect(() => {
        // Only auto-advance in the direction the applicant was already navigating, same as VerifyIncomePageNO/
        // CoApplicantLogInPage -- otherwise a still-valid access token would immediately bounce a Back
        // navigation from the next page straight forward again, making Back a no-op.
        //
        // Requiring applicantGovernmentId too (not just accessToken) distinguishes "already past this step"
        // from "logged in, but extraction never produced a value" -- MitID's `sub` claim missing, empty, or
        // not DK-prefixed. Without this, once any valid token exists the page would advance regardless of
        // whether extraction ever succeeded, and -- since applicantGovernmentId has no other source for the
        // main applicant -- permanently strand it empty with no page left to recover it on (LP-2980).
        //
        // Also requiring !hasError (matching VerifyIncomePageNO/CoApplicantLogInPage's identical guard):
        // nothing in the DK flow sets hasError on this specific field today, but relying on that staying true
        // forever is fragile -- if it ever did, advancing on a flagged-invalid value would reach
        // SubmitPageDK's validation gate, which would just route back here, in the exact same-shape loop the
        // extraction effect below is written to prevent.
        const canAutoAdvance =
            !!accessToken && !!applicantGovernmentIdFieldState?.value && !applicantGovernmentIdFieldState?.hasError;
        if (pageAction === 'back' && canAutoAdvance) {
            defaultPagesAction('back');
        }
        // 'submit'/'continue-with-coapplicant' cover SubmitPageDK's own routing back here: PagesActionButton
        // already set pageAction to whichever of those was clicked, and that's still the value once
        // dispatch(setCurrentPage(...)) lands us back on this page -- `setCurrentPage` only changes which
        // page is shown, not this (separate, local-to-Pages) pageAction state. useDefaultPagesAction only
        // knows how to actually move currentPage for 'next'/'back'/'consolidation-next', so calling it with
        // the original submit-ish action would silently no-op, leaving the applicant stuck on this page even
        // after a successful retry supplies a valid id -- with no banner either, since canAutoAdvance being
        // true means governmentIdMissingAfterLogin is false (LP-2980).
        if (
            (pageAction === 'next' || pageAction === 'submit' || pageAction === 'continue-with-coapplicant') &&
            canAutoAdvance
        ) {
            defaultPagesAction('next');
        }
    }, [pageAction, accessToken, applicantGovernmentIdFieldState?.value, applicantGovernmentIdFieldState?.hasError]);

    useEffect(() => {
        if (!accessToken) {
            return;
        }
        const governmentId = extractGovernmentIdFromSubClaim(accessToken, CountryCode.DK);
        // Compare against the current value rather than only its presence -- InitPage can pre-populate
        // applicantGovernmentId from the deep-link payload before this MitID login ever runs, and a
        // presence-only guard would let that stale value permanently block the authenticated one from ever
        // landing. Once the two agree, this becomes a no-op, so it's still safe to depend on the field state.
        //
        // Also re-dispatching (with hasError explicitly cleared) when the value already matches but hasError
        // is set: a matching value alone would otherwise skip the dispatch entirely, leaving a stale
        // hasError -- inherited from some other flow, e.g. a shared browser session -- with no way to ever
        // clear it, since a freshly authenticated extraction is the one thing here that should be able to
        // override any prior error state on this field (LP-2980).
        if (
            governmentId &&
            (governmentId !== applicantGovernmentIdFieldState?.value || applicantGovernmentIdFieldState?.hasError)
        ) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: AttributeNames.applicantGovernmentId,
                    value: governmentId,
                    hasError: false,
                }),
            );
        }
    }, [accessToken, applicantGovernmentIdFieldState]);

    // A valid MitID login exists, but it never yielded (and nothing had already supplied) a usable
    // applicantGovernmentId -- either the extraction genuinely failed, or it succeeded but left a stale
    // hasError from some other flow unresolved (see the extraction effect above). Distinct from `error`
    // below (which tracks initAuthentication() itself throwing, e.g. before the redirect ever happens): this
    // is a login that succeeded yet still left nothing usable to submit with. Recomputed on every render
    // rather than stored, so it clears itself the moment a fresh, successful login supplies a value (LP-2980).
    const governmentIdMissingAfterLogin =
        !!accessToken && (!applicantGovernmentIdFieldState?.value || !!applicantGovernmentIdFieldState?.hasError);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'mitid-login') {
            if (!hasConsentToAccept) {
                return false;
            }
            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
            // `applicantCreditStatusConsent` reaches the credit decision the same way
            // `coapplicantCreditStatusConsent` does: `dataFormPage` puts it in the submit `values`,
            // `LoanApplicantFactory` persists it on the applicant, and
            // `CreditDecisionRequest.toCustomerInput` deserializes it onto `CustomerInput`, which declares it
            // as a boolean -- so it must be dispatched as one, not the string 'true'. So a value recorded on a
            // path where no consent was shown would be a false consent record on a live credit decision --
            // hence the `hasConsentToAccept` guard above.
            dispatch(addOrUpdateFormField({ fieldName: AttributeNames.applicantCreditStatusConsent, value: true }));
            try {
                setError(false);
                await dispatch(initAuthentication());
            } catch {
                setError(true);
            }
            return false;
        }
        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.eskat-kreditstatus.title')} />
            <Description text={translate('page.eskat-kreditstatus.description')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            {/*
             * Shown for a service error AND for a successful response that carried no consent to accept --
             * the latter has no banner of its own, so without this the applicant faces a page with nothing to
             * tick and a MitID login button that silently refuses. Matches the co-applicant page.
             */}
            {!isConsentLoading && !hasConsentToAccept && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            {(error || governmentIdMissingAfterLogin) && (
                <ErrorMessage
                    title={translate('page.error.title')}
                    description={translate('page.authentication.submit.log.in.error.description')}
                />
            )}
            <AuthenticationErrorMessage />
            <PagesFooter
                actions={['mitid-login']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default EskatKreditStatusConsentPageDK;
